import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  TextField,
  IconButton,
  Button,
  Box,
  InputAdornment,
} from '@mui/material';
import {
  Search as SearchIcon,
  PhotoCamera,
  Upload as UploadIcon,
  Home as HomeIcon,
  ImageSearch,
  CloudSync as SyncIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  PhotoAlbum as AlbumIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const { user, logout } = useAuth();
  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  const handleImageSearch = (e) => {
    const file = e.target.files[0];
    if (file) {
      navigate('/search', { state: { searchImage: file } });
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <AppBar
      position="sticky"
      sx={{
        backgroundColor: '#000000',
        borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
        boxShadow: 'none',
      }}
    >
      <Toolbar sx={{ justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <img
            src="/logo.webp"
            alt="Company Logo"
            style={{
              height: '32px',
              maxWidth: '120px',
              objectFit: 'contain',
            }}
          />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: '#ffffff',
            }}
          >
            AI Photo Gallery
          </Typography>
        </Box>

        <Box
          component="form"
          onSubmit={handleSearch}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flex: 1,
            maxWidth: 600,
            mx: 3,
          }}
        >
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search photos... (e.g., 'medical', 'bone', 'heart', 'anatomy')"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'rgba(255, 255, 255, 0.5)' }} />
                </InputAdornment>
              ),
              sx: {
                backgroundColor: '#1a1a1a',
                borderRadius: 1,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.08)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.15)',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
              },
            }}
          />

          <Button
            type="submit"
            variant="contained"
            sx={{
              backgroundColor: '#333333',
              minWidth: 'auto',
              px: 2,
              '&:hover': {
                backgroundColor: '#444444',
              },
            }}
          >
            <SearchIcon />
          </Button>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <input
            accept="image/*"
            style={{ display: 'none' }}
            id="image-search-input"
            type="file"
            onChange={handleImageSearch}
          />
          <label htmlFor="image-search-input">
            <IconButton
              component="span"
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                '&:hover': {
                  color: '#ffffff',
                  backgroundColor: 'rgba(255, 255, 255, 0.08)'
                }
              }}
            >
              <ImageSearch />
            </IconButton>
          </label>

          <IconButton
            onClick={() => navigate('/')}
            sx={{
              color: location.pathname === '/' ? '#ffffff' : 'rgba(255, 255, 255, 0.7)',
              backgroundColor: location.pathname === '/' ? 'rgba(255, 255, 255, 0.08)' : 'transparent',
              '&:hover': {
                color: '#ffffff',
                backgroundColor: 'rgba(255, 255, 255, 0.08)'
              }
            }}
          >
            <HomeIcon />
          </IconButton>

          <Button
            startIcon={<AlbumIcon />}
            onClick={() => navigate('/albums')}
            variant={location.pathname.startsWith('/albums') ? 'contained' : 'text'}
            sx={{
              backgroundColor: location.pathname.startsWith('/albums') ? '#333333' : 'transparent',
              color: 'rgba(255, 255, 255, 0.8)',
              '&:hover': {
                backgroundColor: location.pathname.startsWith('/albums') ? '#444444' : 'rgba(255, 255, 255, 0.08)',
              },
            }}
          >
            Albums
          </Button>

          <Button
            startIcon={<UploadIcon />}
            onClick={() => navigate('/upload')}
            variant={location.pathname === '/upload' ? 'contained' : 'text'}
            sx={{
              backgroundColor: location.pathname === '/upload' ? '#333333' : 'transparent',
              color: 'rgba(255, 255, 255, 0.8)',
              '&:hover': {
                backgroundColor: location.pathname === '/upload' ? '#444444' : 'rgba(255, 255, 255, 0.08)',
              },
            }}
          >
            Upload
          </Button>

          <Button
            startIcon={<SyncIcon />}
            onClick={() => navigate('/sync')}
            variant={location.pathname === '/sync' ? 'contained' : 'text'}
            sx={{
              backgroundColor: location.pathname === '/sync' ? '#333333' : 'transparent',
              color: 'rgba(255, 255, 255, 0.8)',
              '&:hover': {
                backgroundColor: location.pathname === '/sync' ? '#444444' : 'rgba(255, 255, 255, 0.08)',
              },
            }}
          >
            Sync
          </Button>

          {/* User Info and Logout */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: 20 }} />
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255, 255, 255, 0.8)',
                  fontSize: '0.875rem'
                }}
              >
                {user?.username?.split('@')[0] || 'User'}
              </Typography>
            </Box>

            <IconButton
              onClick={handleLogout}
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                '&:hover': {
                  color: '#ffffff',
                  backgroundColor: 'rgba(255, 255, 255, 0.08)'
                }
              }}
              title="Logout"
            >
              <LogoutIcon />
            </IconButton>
          </Box>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Header;